import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AuthService from '../services/auth';
import { UserInfo } from '../types/auth';

interface AuthContextType {
  userInfo: UserInfo;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo>({
    isAuthenticated: false,
    roles: [],
    isAdmin: false,
    isContributor: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserInfo = async () => {
    setIsLoading(true);
    try {
      const info = await AuthService.getUserInfo();
      setUserInfo(info);
    } catch (error) {
      console.error('Error loading user info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const refresh = async () => {
    await fetchUserInfo();
  };

  return (
    <AuthContext.Provider value={{ userInfo, isLoading, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};
